import { registerSW } from 'virtual:pwa-register';
import { useSyncExternalStore } from 'react';

// Prompted, never silent — a reload mid-review would throw away the moment.
//
// V4 hotfix: the previous `apply?.(true)` was a no-op when the waiting
// service worker had already been claimed (clientsClaim() in sw.js means
// a new worker takes control the moment it activates; between the banner
// showing and the person tapping Reload, `registration.waiting` can
// disappear). The Reload button then did nothing. Now:
//
//   1. Try the workbox path (skipWaiting + controllerchange reload).
//   2. Race a 2-second timeout — if the controllerchange path doesn't
//      complete in time, unregister every SW, delete every cache, and
//      hard-reload. The person tapped Reload; nothing should keep them
//      on the old shell.
let updateReady = false;
let apply: ((reload?: boolean) => Promise<void>) | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function startServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  apply = registerSW({
    immediate: true,
    onNeedRefresh() {
      updateReady = true;
      emit();
    },
  });
}

export function useUpdateReady(): boolean {
  return useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => updateReady,
  );
}

export async function applyUpdate(): Promise<void> {
  // Kick the workbox path — best case, this reloads within a beat.
  void apply?.(true);
  // Race a hard fallback: if we're still here after 2 seconds, the SW
  // machinery isn't cooperating (waiting worker gone, message ignored,
  // controllerchange never fires on some phones). Nuke and reload.
  window.setTimeout(() => {
    void hardReload();
  }, 2000);
}

export async function hardReload(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => undefined)));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => undefined)));
    }
  } catch {
    // Best-effort — even a partial wipe is better than staying stuck.
  }
  // Cache-buster in the URL so no intermediate proxy hands us the old
  // shell either.
  const url = new URL(window.location.href);
  url.searchParams.set('_r', String(Date.now()));
  window.location.replace(url.toString());
}
